import type { NotificationExpression } from "./notificationPresentation.js";
/** Optional source-owned display metadata beside unchanged canonical values. */
export type StreamingFeedbackDescriptor = Omit<NotificationExpression, "params"> & { params: Record<string, string | number> };
export type StreamingFeedbackFields = { messagePresentation?: StreamingFeedbackDescriptor | undefined; validationPresentation?: Array<StreamingFeedbackDescriptor | null> | undefined };

export type StreamingTitleSource = { version: 1; code: string; contentHash: string };
const titleSources: Readonly<Record<string, { text: string; hash: string }>> = {
  "streamingFeedback.names.unknownMovie": {
    "text": "Unknown Movie",
    "hash": "b0e3ffd20e9b0829f6124826d5f151689e136fb5102923720b567b2d3a786398"
  },
  "streamingFeedback.names.unknownSeries": {
    "text": "Unknown Series",
    "hash": "6a7327c3275920d82e22f9c8e3f815f38d36cf4a66f424412206d1407fa3fdcc"
  },
  "streamingFeedback.names.unknownChannel": {
    "text": "Unknown Channel",
    "hash": "6b811e57fa858c771592498d5e1f1accfdd412e7d5203f66c9370c100d23f1e3"
  },
  "streamingFeedback.names.unknownAudio": {
    "text": "Unknown Audio",
    "hash": "bb1ef3e739e035ba29ff9763c819c1b1d73515198da06543fb556776534d89b5"
  },
  "streamingFeedback.names.unknownMedia": {
    "text": "Unknown Media",
    "hash": "90d762826b7a848d0d0b02779a45ab027f9c24a853e60616c1251ba0f0185741"
  }
};
const ownTitleKey = (o: object, key: string): boolean => Object.prototype.hasOwnProperty.call(o, key);
/** Validate fixed source titles against both their explicit identity and UTF-8 digest. */
export function readStreamingTitleSource(title: unknown, source: unknown): StreamingFeedbackDescriptor | undefined {
  if (!source || typeof source !== "object" || Array.isArray(source) || (Object.getPrototypeOf(source) !== Object.prototype && Object.getPrototypeOf(source) !== null)) return undefined;
  const value = source as Record<string, unknown>;
  if (Object.keys(value).sort().join(",") !== "code,contentHash,version" || value.version !== 1 || typeof value.code !== "string" || !ownTitleKey(titleSources, value.code)) return undefined;
  const expected = titleSources[value.code]!;
  return title === expected.text && value.contentHash === expected.hash ? { code: value.code, params: {} } : undefined;
}
export function createStreamingTitleSource(code: string): StreamingTitleSource | undefined {
  return ownTitleKey(titleSources, code) ? { version: 1, code, contentHash: titleSources[code]!.hash } : undefined;
}
/** Strip stale or unknown presentation while keeping every canonical media field. */
export function validatedStreamingMediaDetails<T>(details: T): T {
  if (!details || typeof details !== "object" || Array.isArray(details)) return details;
  const object = details as Record<string, unknown>;
  const { titleSource, titlePresentation: _unbound, ...raw } = object;
  return { ...raw, ...(readStreamingTitleSource(object.title, titleSource) ? { titleSource } : {}) } as T;
}
